import React from 'react';
import { PrintBlockSection } from './PrintBlockSection';
import type { PrintBlockRow } from './printBlockRows';
import { StaggerReveal } from '../../components/ui/StaggerReveal';
import type { EvaluationBlock } from '@interactive-room';
import type { ExamBlockExamContext } from '../components/ExamBlockInlineEditor';

interface PrintBlockGridProps {
  rows: PrintBlockRow[];
  subjectTag?: string;
  isStudyGuide?: boolean;
  editable?: boolean;
  revealStagger?: boolean;
  examContextForIndex?: (index: number) => ExamBlockExamContext | undefined;
  onBlockChange?: (block: EvaluationBlock) => void;
}

export function PrintBlockGrid({
  rows,
  subjectTag,
  isStudyGuide,
  editable = false,
  revealStagger = false,
  examContextForIndex,
  onBlockChange,
}: PrintBlockGridProps) {
  let staggerIndex = 0;

  const wrapStagger = (node: React.ReactNode, key: string) => {
    if (!revealStagger) {
      return <React.Fragment key={key}>{node}</React.Fragment>;
    }
    const currentIndex = staggerIndex;
    staggerIndex += 1;
    return (
      <StaggerReveal key={key} index={currentIndex} enabled>
        {node}
      </StaggerReveal>
    );
  };

  return (
    <>
      {rows.map((row) => {
        if (row.kind === 'pair') {
          const [left, right] = row.blocks;
          return (
            <div
              key={`pair-${left.id}-${right.id}`}
              className="print-blocks-grid print:break-inside-avoid"
            >
              {wrapStagger(
                <PrintBlockSection
                  block={left}
                  index={row.indices[0]}
                  subjectTag={subjectTag}
                  isStudyGuide={isStudyGuide}
                  layout="grid-cell"
                  editable={editable}
                  examContext={examContextForIndex?.(row.indices[0])}
                  onBlockChange={onBlockChange}
                />,
                `stagger-${left.id}`,
              )}
              {wrapStagger(
                <PrintBlockSection
                  block={right}
                  index={row.indices[1]}
                  subjectTag={subjectTag}
                  isStudyGuide={isStudyGuide}
                  layout="grid-cell"
                  editable={editable}
                  examContext={examContextForIndex?.(row.indices[1])}
                  onBlockChange={onBlockChange}
                />,
                `stagger-${right.id}`,
              )}
            </div>
          );
        }

        return wrapStagger(
          <PrintBlockSection
            block={row.block}
            index={row.index}
            subjectTag={subjectTag}
            isStudyGuide={isStudyGuide}
            layout="full"
            editable={editable}
            examContext={examContextForIndex?.(row.index)}
            onBlockChange={onBlockChange}
          />,
          `stagger-${row.block.id}`,
        );
      })}
    </>
  );
}
